import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Map,
  InfoWindow,
  AdvancedMarker,
  useMap,
} from "@vis.gl/react-google-maps";
import "./HaqtMap.scss";
import { SYSTEM_NOT_AVAILABLE } from "../../pages/MapView/MapConstants";
import InfoWindowContent from "../../pages/MapView/InfoWindow";

// Constants
const MIN_ZOOM_LEVEL = 1;
const ZOOM_LEVEL_FOR_SINGLE_MARKER = 2;
const ALERT_LEVEL = {
  CRITICAL: "critical",
  WARNING: "warning",
  NORMAL: "normal",
};

// HaqtMap component
const HaqtMap = ({
  positions,
  onMarkerClick,
  selectedCenter,
  // Dheeraj Edit - removed
  // onMarkerMouseOver,
  // onMarkerMouseOut,
  // infoWindow,
  // selectedMarker,
  // setSelectedMarker,
}) => {
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [infowindowOpen, setInfowindowOpen] = useState(false);
  const [infoWindowHovered, setInfowindowHovered] = useState(false);
  // Dheeraj Edit
  const [currentCenter, setCurrentCenter] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(3);
  //
  const map = useMap();
  const mapId = process.env.REACT_APP_GOOGLE_MAP_ID;
  // Dheeraj Edit - added
  const timeoutIdRef = useRef(null);
  useEffect(() => {
    if (!map) return;
    if (positions?.length > 0 && map) {
      setInfowindowOpen(false);
      // fitMapToBounds(positions); // Dheeraj Edit - removed
    }
  }, [map, positions]);

  // Fit map to markers
  const fitMapToBounds = (positions) => {
    const markers = positions.map((marker, index) => ({
      key: index,
      position: { lat: marker.lat, lng: marker.lng },
      city: marker.city,
      address: marker.address,
    }));
    if (markers.length > 0 && map) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((marker) => bounds.extend(marker.position));
      map.fitBounds(bounds);
      if (markers.length === 1) {
        map.setZoom(ZOOM_LEVEL_FOR_SINGLE_MARKER);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  //from latAndLngs we will get lat and lng of all locations
  const latAndLngs = positions?.map((marker) => ({
    lat: marker.lat,
    lng: marker.lng,
  }));

  // avgLat and avgLng will give us average lat and average lng based on all latAndLngs
  const avgLat =
    latAndLngs?.reduce((total, pos) => total + pos.lat, 0) /
      latAndLngs?.length || 20.5937;
  // const avgLng =
  //   latAndLngs?.reduce((total, pos) => total + pos.lng, 0) /
  //     latAndLngs?.length || 20.5937;

  // Dheeraj Edit - added

  // Add an offset to shift view eastward (positive number shifts east)
  const LONGITUDE_OFFSET = 60; // Adjust this value to shift more or less
  const avgLng =
    latAndLngs?.reduce((total, pos) => total + pos.lng, 0) /
      latAndLngs?.length || 20.5937 + LONGITUDE_OFFSET;

  // Handle marker click
  const handleMarkerClick = (site) => {
    // setSelectedMarker && setSelectedMarker(site); // Dheeraj Edit - removed
    onMarkerClick?.(site);
  };

  // Marker background color based on status
  const getMarkerBackground = (status, deviceStatus) => {
    if (deviceStatus === "Inactive") {
      return "#A6A6A6"; //Gray for inactive
    }
    switch (status) {
      case ALERT_LEVEL.CRITICAL:
        return "#EA4335";
      case ALERT_LEVEL.WARNING:
        return "#FDBF2D";
      case ALERT_LEVEL.NORMAL:
        return "#2CB82B";
      default:
        return "#A6A6A6"; // Default grey
    }
  };

  //Marker inner dot color based on status (Dheeraj Edit)
  const getMarkerInnerCircle = (status, deviceStatus) => {
    if (deviceStatus === "Inactive") {
      return "#636262"; //Gray for inactive
    }
    switch (status) {
      case "critical":
        return "#B41412";
      case "warning":
        return "#c99b2c";
      case "normal":
        return "#007C00";
      default:
        return "#A6A6A6";
    }
  };

  // Dheeraj Edit

  const handleMouseOver = (site) => {
    if (site && site.lat && site.lng) {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      setHoveredMarker(site);
      setInfowindowOpen(true);
    }
  };

  const closeInfoWindow = () => {
    setHoveredMarker(null);
    setInfowindowOpen(false);
    setInfowindowHovered(false);
  };

  const handleMouseOut = () => {
    timeoutIdRef.current = setTimeout(() => {
      if (!infoWindowHovered) {
        closeInfoWindow();
      }
    }, 200);
  };

  const handleInfoStateChange = (isHovered) => {
    setInfowindowHovered(isHovered);
    if (isHovered && timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    } else if (!isHovered) {
      timeoutIdRef.current = setTimeout(() => {
        closeInfoWindow();
      }, 200);
    }
  };

  const infoWindow = hoveredMarker && (
    <InfoWindowContent
      selectedMarker={hoveredMarker}
      onInfoStateChange={handleInfoStateChange}
    />
  );

  // Function to handle map positioning based on category
  const handleMapPositioning = async (map, selectedCenter) => {
    if (!map) return;

    if (!selectedCenter?.center) {
      setCurrentCenter(null);
      map.panTo({ lat: avgLat, lng: avgLng });
      map.setZoom(3);
      return;
    }

    const { center, category } = selectedCenter;
    const geocoder = new window.google.maps.Geocoder();

    switch (category) {
      case "country":
      case "city": {
        try {
          const response = await geocoder.geocode({
            address: center.name || center.address,
          });

          if (response.results?.[0]?.geometry) {
            const bounds =
              response.results[0].geometry.bounds ||
              response.results[0].geometry.viewport;

            if (bounds) {
              map.fitBounds(bounds);
              // Optional: ensure zoom isn't too close or too far
              const listener = map.addListener("idle", () => {
                if (category === "city" && map.getZoom() > 13) {
                  map.setZoom(13);
                }
                if (category === "country" && map.getZoom() > 7) {
                  map.setZoom(7);
                }
                window.google.maps.event.removeListener(listener);
              });
            }
            setCurrentCenter(center);
          }
        } catch (error) {
          console.error("Geocoding error:", error);
          // Fallback to provided coordinates if geocoding fails
          map.panTo({ lat: center.lat, lng: center.lng });
          map.setZoom(category === "country" ? 5 : 10);
        }
        break;
      }
      case "tenant": {
        setCurrentCenter(center);
        map.panTo({ lat: center.lat, lng: center.lng });
        map.setZoom(4);
        break;
      }
      case "site":
      case "device": {
        setCurrentCenter(center);
        map.panTo({ lat: center.lat, lng: center.lng });
        map.setZoom(16);
        break;
      }
      default: {
        setCurrentCenter(center);
        map.panTo({ lat: center.lat, lng: center.lng });
        map.setZoom(12);
      }
    }
  };

  // useEffect to trigger map positioning
  useEffect(() => {
    handleMapPositioning(map, selectedCenter);
  }, [selectedCenter, map]);

  return (
    <div className="haqt-map">
      <Map
        clickableIcons={false}
        className="vismap"
        mapId={mapId}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          mapTypeControl: true, // Dheeraj Edit - added
          mapTypeControlOptions: {
            style: 1,
            position: 6, // 1&5 is top left, 3 is top right, 6 is above Google,
            mapTypeIds: ["roadmap", "hybrid"], //"satellite", "terrain"
          },
        }}
        defaultZoom={zoomLevel}
        minZoom={MIN_ZOOM_LEVEL}
        defaultCenter={{ lat: avgLat, lng: avgLng }}
        gestureHandling={"greedy"}
        disableDefaultUI
      >
        {positions?.map((site, index) => (
          <AdvancedMarker
            key={index}
            position={{ lat: site.lat, lng: site.lng }}
            title={site.name}
            onClick={() => {
              handleMarkerClick(site);
            }}
          >
            {/* Marker container with hover logic */}
            <div
              className="marker-container"
              onMouseOver={() => handleMouseOver(site)}
              onMouseOut={() => handleMouseOut(site)}
            >
              {/* Styling for Site Pin */}
              <div
                style={{
                  position: "relative",
                  width: "30px", // Width of the pin body
                  height: "30px", // Height of the pin body (slightly taller)
                  backgroundColor: getMarkerBackground(
                    site.siteStatus,
                    site.devices[0].deviceStatus,
                  ), // Background color based on status
                  borderRadius: "50% 50% 50% 50%", // Rounded top corners (Pin body)
                  transform: "translateX(-50%)", // Centers the pin
                  boxShadow: "0 2px 4px rgba(0,0,0,0.3)", // Optional: adds shadow for a more realistic effect
                  cursor: "pointer",
                  marginBottom: "15px", // Add space for the rotated tip
                }}
              >
                {/* Pin Tip - Bottom Triangle (Pointed Tip) */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "-10px",
                    left: "50%",
                    width: "20px",
                    height: "15px",
                    backgroundColor: getMarkerBackground(
                      site.siteStatus,
                      site.devices[0].deviceStatus,
                    ),
                    clipPath: "polygon(50% 100%, 0 0, 100% 0)", // Creates triangle
                    transform: "translateX(-50%)",
                  }}
                />

                {/* Optional: White center dot inside the pin */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: "13px",
                    height: "13px",
                    backgroundColor: getMarkerInnerCircle(
                      site.siteStatus,
                      site.devices[0].deviceStatus,
                    ),
                    borderRadius: "50%",
                    transform: "translate(-50%, -50%) rotate(45deg)", // Counter-rotate the inner circle
                  }}
                />
              </div>
            </div>
          </AdvancedMarker>
        ))}
        {/* InfoWindow display on hover */}
        {/* Dheeraj Edit - removed hoveredMarker &&  */}
        {infowindowOpen && (
          <InfoWindow
            position={{
              lat: hoveredMarker.lat,
              lng: hoveredMarker.lng,
            }}
            options={{
              pixelOffset: new window.google.maps.Size(0, -35),
            }}
            // Dheeraj Edit - removed
            // onCloseClick={() => {
            //   setInfowindowOpen(false);
            // }}
          >
            {infoWindow || <div>{hoveredMarker.name}</div>}
          </InfoWindow>
        )}
      </Map>
    </div>
  );
};

// Prop types for the component
HaqtMap.propTypes = {
  positions: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
      status: PropTypes.string,
    }),
  ).isRequired,
  onMarkerClick: PropTypes.func,
  onMarkerMouseOver: PropTypes.func,
  onMarkerMouseOut: PropTypes.func,
  infoWindow: PropTypes.node,
  selectedMarker: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  setSelectedMarker: PropTypes.func,
};

export default HaqtMap;
